import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useVirtualWorld from "../../hooks/useVirtualWorld";
import vw from "../../api/virtualWorld.api";
import MatchTicker from "../../components/VirtualWorld/MatchTicker";
import MatchReportCard from "../../components/VirtualWorld/MatchReportCard";
import MatchResultScreen from "../../components/VirtualWorld/MatchResultScreen";
import MatchHighlights from "../../components/VirtualWorld/MatchHighlights";
import MatchdayProgress from "../../components/VirtualWorld/MatchdayProgress";
import Spielberichtsbogen from "../../components/VirtualWorld/Spielberichtsbogen";
import SeasonEndOverlay from "../../components/VirtualWorld/SeasonEndOverlay";
import CupCeremonyOverlay from "../../components/VirtualWorld/CupCeremonyOverlay";
import { formatManagerDate, relativeUntil } from "../../helper/managerDate";
import Loading from "../../components/Loading";

const COMP_META = [
  { id: "friendly", label: "Freundschaftsspiel", emoji: "🍻", short: "Kennlernen ohne Ligadruck" },
  { id: "regular", label: "Reguläre Liga", emoji: "🎯", short: "Divisionsspieltag · Tabellenpunkte" },
  { id: "flowliga", label: "Team-FlowLiga", emoji: "📉", short: "Restpunkte über alle Teams" },
  { id: "tournament", label: "Team-Turnier", emoji: "🏅", short: "K.-o. gegen Startgeld" },
];

const money = (n) => `${Number(n || 0).toLocaleString("de-DE")} €`;

function scoringOf(match, tabCompetition) {
  if (match?.competition === "flowliga") return "rest";
  if (match?.competition === "tournament") return "checkout";
  if (match?.competition === "regular" || match?.competition === "friendly") return "bo5";
  if (match?.scoring === "rest" || match?.scoring === "checkout") return match.scoring;
  if (tabCompetition === "flowliga") return "rest";
  if (tabCompetition === "tournament") return "checkout";
  return "bo5";
}

function scoreLabel(match, hideScore, tabCompetition) {
  if (hideScore) return "Live — Ergebnis kommt nach dem letzten Dart.";
  if (match?.status !== "finished") {
    return tabCompetition === "friendly" ? "Freundschaftsspiel" : `Spieltag ${match?.matchday}`;
  }
  const scoring = scoringOf(match, tabCompetition);
  if (scoring === "rest") {
    return `Flow-Punkte ${match.scoreHome}:${match.scoreAway} (Gegner-Rest nach Checkout — mehr gewinnt)`;
  }
  if (scoring === "checkout") return `Checkout-Summe ${match.scoreHome}:${match.scoreAway}`;
  return `Spiele ${match.scoreHome}:${match.scoreAway} (BO5, first-to-3)`;
}

const ManagerMatch = () => {
  const { userId, manager, loading, refresh, error } = useVirtualWorld();
  const navigate = useNavigate();
  const comps = manager?.competitions || manager?.progression?.manager?.competitions || {};
  const phase = Number(manager?.onboardingPhase || manager?.progression?.manager?.onboardingPhase || comps.phase || 1);
  const guided = Boolean(manager?.guided ?? manager?.progression?.manager?.guided);
  const guide = manager?.guide || manager?.progression?.manager?.guide;
  const guideComp = guide?.current?.competition;
  const leagueStartedEarly = Boolean(comps.leagueStarted);
  const awaitingFirstLeague =
    Boolean(manager?.club?.firstTrainingDoneAt) &&
    Number(manager?.club?.played || 0) < 1 &&
    leagueStartedEarly;
  const primary =
    guideComp ||
    (guide?.current?.key === "league_play" || awaitingFirstLeague ? "regular" : null) ||
    comps.primary ||
    (phase <= 2 ? "friendly" : "regular");

  const [competition, setCompetition] = useState(primary);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [live, setLive] = useState(false);
  const [revealed, setRevealed] = useState(true);
  /** Nach Spiel: Ergebnis halten, nächsten Gegner erst nach „Weiter“ laden */
  const [holdingResult, setHoldingResult] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [playKey, setPlayKey] = useState(0);
  const [lineupBusy, setLineupBusy] = useState(false);
  const [leaguePeek, setLeaguePeek] = useState(null);
  const [showResultDetails, setShowResultDetails] = useState(false);
  const [matchdayBoard, setMatchdayBoard] = useState(null);
  const [boardPolling, setBoardPolling] = useState(false);
  const [seasonEnd, setSeasonEnd] = useState(null);
  const [cupCeremonyLocal, setCupCeremonyLocal] = useState(null);
  const sheetRef = useRef(null);
  const resultTopRef = useRef(null);
  /** Nutzer hat Wettbewerb aktiv gewählt — nicht nach jedem Match zurück auf Liga stehlen. */
  const userPickedComp = useRef(false);

  const pickCompetition = useCallback((id) => {
    userPickedComp.current = true;
    setCompetition(id);
  }, []);

  const isOpen = useCallback(
    (id) => {
      if (id === "friendly") return comps.friendly !== false;
      if (id === "regular") return Boolean(comps.regular);
      if (id === "flowliga") return Boolean(comps.flowliga);
      if (id === "tournament") return Boolean(comps.tournament);
      return false;
    },
    [comps]
  );

  useEffect(() => {
    // Ergebnis-Maske / manuelle Tab-Wahl nicht stehlen (sonst landet man nach Turnier wieder in der Liga)
    if (holdingResult || live || userPickedComp.current) return;
    // Erster Ligaspieltag: immer Regular, nicht versehentlich Freundschaft
    if (
      (guide?.current?.key === "league_play" || awaitingFirstLeague) &&
      isOpen("regular")
    ) {
      setCompetition("regular");
      return;
    }
    if (isOpen(primary)) setCompetition(primary);
  }, [primary, isOpen, holdingResult, live, guide?.current?.key, awaitingFirstLeague]);

  const load = useCallback(() => {
    if (!userId || !isOpen(competition)) {
      setPreview(null);
      return Promise.resolve();
    }
    return vw
      .nextMatch(userId, competition)
      .then((res) => {
        setPreview(res.data);
        if (res.data?.seasonCeremony) {
          setSeasonEnd({
            ceremony: res.data.seasonCeremony,
            review: res.data.seasonReview || null,
          });
        }
      })
      .catch(() => setPreview(null));
  }, [userId, competition, isOpen]);

  // Preview nur bei Wettbewerb-/User-Wechsel neu laden — nie während Ergebnis-Ansicht.
  // Achtung: nicht `isOpen` als Dependency (neue Identity nach refresh → Ergebnis verschwindet).
  const competitionOpen = isOpen(competition);
  useEffect(() => {
    if (holdingResult || live) return undefined;
    setResult(null);
    setLive(false);
    setRevealed(true);
    setMsg("");
    setPreviewLoading(true);
    if (!userId || !competitionOpen) {
      setPreview(null);
      setPreviewLoading(false);
      return undefined;
    }
    let cancelled = false;
    vw.nextMatch(userId, competition)
      .then((res) => {
        if (cancelled) return;
        setPreview(res.data);
        if (res.data?.seasonCeremony) {
          setSeasonEnd({
            ceremony: res.data.seasonCeremony,
            review: res.data.seasonReview || null,
          });
        }
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [competition, userId, competitionOpen, holdingResult, live]);

  const continueToNext = useCallback(async () => {
    setBusy(true);
    setMsg("");
    try {
      const home = await refresh();
      const guidedNow = Boolean(home?.guided ?? home?.progression?.manager?.guided);
      const g = home?.guide?.current || home?.progression?.manager?.guide?.current;
      const trainDone = Boolean(home?.club?.firstTrainingDoneAt);
      const leagueOn = Boolean(
        home?.competitions?.leagueStarted || home?.progression?.manager?.competitions?.leagueStarted
      );

      setResult(null);
      setMatchdayBoard(null);
      setHoldingResult(false);
      setLive(false);
      setRevealed(true);

      // Cup / FlowLiga: im gleichen Wettbewerb bleiben (kein Zwangswechsel zur Liga)
      if (competition === "tournament" || competition === "flowliga") {
        userPickedComp.current = true;
        await load();
        return;
      }

      // Nach erstem Spiel: Onboarding erklären, nicht sofort nächstes Match laden
      if (guidedNow) {
        if (!trainDone || g?.key === "training") {
          navigate("/manager/training");
          return;
        }
        if (g?.to && g.to !== "/manager/match") {
          navigate(g.to);
          return;
        }
        if (g?.key === "league_play" || (trainDone && leagueOn && competition === "friendly")) {
          setCompetition("regular");
          return;
        }
      }

      if (competition === "friendly" && leagueOn && trainDone) {
        setCompetition("regular");
        return;
      }
      await load();
    } catch (err) {
      setMsg(err?.data?.message || err?.message || "Weiter geht gerade nicht.");
    } finally {
      setBusy(false);
    }
  }, [refresh, load, competition, navigate]);

  // Nach Freundschaft: Gegner fürs Ligaspiel vorladen (Abhol-Text)
  useEffect(() => {
    if (!holdingResult || !userId || !comps.leagueStarted || competition !== "friendly") {
      setLeaguePeek(null);
      return undefined;
    }
    let cancelled = false;
    vw.nextMatch(userId, "regular")
      .then((res) => {
        if (!cancelled) setLeaguePeek(res.data);
      })
      .catch(() => {
        if (!cancelled) setLeaguePeek(null);
      });
    return () => {
      cancelled = true;
    };
  }, [holdingResult, userId, comps.leagueStarted, competition]);

  const onTickerDone = useCallback(() => {
    setRevealed(true);
    setLive(false);
    if (result?.cupCeremony) {
      setCupCeremonyLocal(result.cupCeremony);
    }
    if (result?.seasonCeremony) {
      setSeasonEnd({
        ceremony: result.seasonCeremony,
        review: result.seasonReview || null,
      });
      setHoldingResult(false);
    } else {
      setHoldingResult(true);
    }
  }, [result?.seasonCeremony, result?.seasonReview, result?.cupCeremony]);

  // Bericht + Weiter-CTA: nach oben scrollen, Details standardmäßig zu
  useEffect(() => {
    if (!holdingResult) return undefined;
    setShowResultDetails(false);
    const t = window.setTimeout(() => {
      resultTopRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 80);
    return () => clearTimeout(t);
  }, [holdingResult, playKey]);

  // Layout übernimmt manager.seasonCeremony — lokalen Rest löschen (sonst Zombie nach neuer Saison).
  useEffect(() => {
    if (manager?.seasonCeremony && seasonEnd?.ceremony) {
      setSeasonEnd(null);
    }
  }, [manager?.seasonCeremony, seasonEnd?.ceremony]);

  useEffect(() => {
    if (manager?.cupCeremony && cupCeremonyLocal) {
      setCupCeremonyLocal(null);
    }
  }, [manager?.cupCeremony, cupCeremonyLocal]);

  const openList = useMemo(() => COMP_META.filter((c) => isOpen(c.id)), [isOpen]);
  const lockedList = useMemo(() => COMP_META.filter((c) => !isOpen(c.id)), [isOpen]);

  // Während Ergebnis-Ansicht: result.match; zum Anpfiff nur scheduled aus preview
  const match = holdingResult || live
    ? result?.match || preview?.match
    : preview?.match?.status === "scheduled"
      ? preview.match
      : result?.match?.status === "scheduled"
        ? result.match
        : preview?.match || result?.match;
  const report = result?.report || preview?.report;
  const ticker = match?.ticker || [];
  const hideScore = live && !revealed;
  const clubId = manager?.club?._id || manager?.club?.id;
  const idOf = (v) => {
    if (v == null) return "";
    if (typeof v === "object") return String(v._id || v.id || "");
    return String(v);
  };
  const myIsHome = match && clubId
    ? idOf(match.homeClubId) === idOf(clubId)
    : preview?.isHome !== false;
  const mySide = myIsHome ? "home" : "away";
  const incomeSide = myIsHome ? match?.incomeHome : match?.incomeAway;
  const moneyBreakdownDisplay = (() => {
    const bd = report?.moneyBreakdown;
    if (bd && (Number(bd.homeCrowd) > 0 || Number(bd.leaguePayout) > 0 || Number(bd.sponsorShare) > 0)) {
      return bd;
    }
    if (!incomeSide) return null;
    return {
      leaguePayout: Number(incomeSide.leaguePayout) || 0,
      homeCrowd: Number(incomeSide.homeCrowd) || 0,
      sponsorShare: Number(incomeSide.sponsorShare) || 0,
      wages: Number(incomeSide.wages) || 0,
      material: Number(incomeSide.material) || 0,
    };
  })();
  /** Brutto-Spieltag: Report zuerst, sonst Match-income (falls Report-Felder fehlen). */
  const moneyTotalDisplay = (() => {
    const fromReport =
      Number(report?.moneyTotal) > 0
        ? Number(report.moneyTotal)
        : Number(report?.moneyEarned) > 0
          ? Number(report.moneyEarned)
          : 0;
    if (fromReport > 0) return fromReport;
    const fromMatch = Number(incomeSide?.total) || 0;
    if (fromMatch > 0) return fromMatch;
    const fromParts =
      (Number(moneyBreakdownDisplay?.leaguePayout) || 0) +
      (Number(moneyBreakdownDisplay?.homeCrowd) || 0) +
      (Number(moneyBreakdownDisplay?.sponsorShare) || 0);
    return fromParts > 0 ? fromParts : null;
  })();
  const moneyNetDisplay =
    report?.moneyNet != null && report.moneyNet !== ""
      ? Number(report.moneyNet)
      : incomeSide?.net != null && incomeSide.net !== ""
        ? Number(incomeSide.net)
        : null;
  const needEntry = competition === "tournament" && preview && !preview.tournamentEntered;
  const matchFinished = match?.status === "finished" && !hideScore;
  const hasLineup = (manager?.club?.starterKeys || []).length >= 4;
  const playableScheduled = preview?.match?.status === "scheduled";
  const canPlay =
    playableScheduled &&
    !needEntry &&
    !live &&
    !holdingResult &&
    isOpen(competition) &&
    hasLineup;
  const guideNext = guide?.current;
  const showTrainingCta =
    (competition === "friendly" || guideNext?.key === "training") &&
    matchFinished &&
    (!manager?.club?.firstTrainingDoneAt || guideNext?.key === "training");
  const activeMeta = COMP_META.find((c) => c.id === competition) || COMP_META[0];
  const firstFriendlySetup =
    guided &&
    competition === "friendly" &&
    !holdingResult &&
    !live &&
    !matchFinished &&
    Number(manager?.club?.played || 0) < 1 &&
    !manager?.club?.friendlyPlayedAt;
  const showPreMatchSheet =
    Boolean(manager?.club) &&
    !holdingResult &&
    !live &&
    isOpen(competition) &&
    !needEntry &&
    !preview?.eliminated &&
    !preview?.waitingForNextRound &&
    (playableScheduled || firstFriendlySetup || previewLoading);

  // Erstes Freundschaftsspiel: direkt zum Bogen scrollen
  useEffect(() => {
    if (!firstFriendlySetup || !showPreMatchSheet) return undefined;
    const t = window.setTimeout(() => {
      sheetRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
    return () => clearTimeout(t);
  }, [firstFriendlySetup, showPreMatchSheet, competition]);

  const leagueStarted = Boolean(comps.leagueStarted);
  const firstCompetitionPhase = !leagueStarted && phase <= 2;

  const journeySteps = useMemo(() => {
    const trainDone = Boolean(manager?.club?.firstTrainingDoneAt);
    const friendlyDone = Boolean(manager?.club?.friendlyPlayedAt) || matchFinished;
    const leagueDone = Number(manager?.club?.played || 0) >= 1;
    return [
      {
        key: "friendly",
        label: "Freundschaft",
        done: friendlyDone,
        current: holdingResult && competition === "friendly" && !trainDone,
      },
      {
        key: "training",
        label: "Training",
        done: trainDone,
        current: friendlyDone && !trainDone,
      },
      {
        key: "league",
        label: "Ligaspiel",
        done: leagueDone,
        current: Boolean(trainDone && leagueStarted && !leagueDone),
      },
    ];
  }, [manager, matchFinished, competition, leagueStarted, holdingResult]);

  // Spieltag-Board pollen, solange Ergebnis gehalten wird und noch Partien offen sind (bis Mitternacht).
  useEffect(() => {
    if (!holdingResult || !userId) return undefined;
    const matchId = result?.match?.id || result?.match?._id;
    if (!matchId || competition === "friendly") return undefined;
    if (matchdayBoard?.complete) return undefined;
    const until = matchdayBoard?.refreshUntil ? new Date(matchdayBoard.refreshUntil).getTime() : 0;
    if (until && Date.now() >= until) return undefined;

    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (until && Date.now() >= until) {
        setBoardPolling(false);
        return;
      }
      setBoardPolling(true);
      try {
        const res = await vw.matchdayBoard(userId, matchId);
        if (!cancelled && res.data) setMatchdayBoard(res.data);
      } catch (_) {
        /* still try later */
      } finally {
        if (!cancelled) setBoardPolling(false);
      }
    };

    const id = setInterval(tick, 12000);
    const first = setTimeout(tick, 4000);
    return () => {
      cancelled = true;
      clearInterval(id);
      clearTimeout(first);
    };
  }, [
    holdingResult,
    userId,
    competition,
    result?.match?.id,
    result?.match?._id,
    matchdayBoard?.complete,
    matchdayBoard?.refreshUntil,
  ]);

  if (loading && !manager) return <Loading />;
  if (!manager?.club) {
    return (
      <div className="space-y-2">
        {error ? (
          <p className="text-red-600 font-semibold">Manager-Daten laden fehlgeschlagen: {error}</p>
        ) : null}
        <p>Bitte zuerst einen Club gründen.</p>
        <p className="text-sm opacity-70">
          Wenn du schon einen Club hattest: einmal hart neu laden, Login prüfen. Der Test-Button „TEMP · Reset“
          löscht den Club wirklich — den nicht aus Versehen drücken.
        </p>
        <button type="button" className="underline text-sm" onClick={() => refresh()}>
          Erneut laden
        </button>
      </div>
    );
  }

  const play = async (mode) => {
    if (!hasLineup) {
      setMsg("Bitte zuerst die Aufstellung H1–H4 auf dem Spielberichtsbogen speichern.");
      sheetRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    setBusy(true);
    setMsg("");
    try {
      const res = await vw.playMatch(userId, mode, competition);
      setPlayKey((k) => k + 1);
      setResult(res.data);
      if (res.data?.matchdayBoard) setMatchdayBoard(res.data.matchdayBoard);
      else setMatchdayBoard(null);
      if (res.data?.seasonCeremony) {
        setSeasonEnd({
          ceremony: res.data.seasonCeremony,
          review: res.data.seasonReview || null,
        });
      }
      if (res.data?.cupCeremony) {
        setCupCeremonyLocal(res.data.cupCeremony);
      }
      const already = Boolean(res.data?.alreadyPlayed);
      const isLive = mode === "live" && !already;
      setLive(isLive);
      setRevealed(!isLive);
      if (!isLive) {
        // Ergebnis halten; bei Saisonende liegt die Zeremonie sofort darüber (kein „Nächstes Spiel“)
        // Cup-Zeremonie liegt als Overlay darüber — Ergebnis darunter behalten.
        setHoldingResult(!res.data?.seasonCeremony);
        setBusy(false);
        void refresh();
        return;
      }
    } catch (err) {
      setMsg(err?.data?.message || err?.message || "Match konnte nicht gestartet werden.");
    } finally {
      setBusy(false);
    }
  };

  const enter = async () => {
    setBusy(true);
    setMsg("");
    try {
      await vw.enterTournament(userId);
      await refresh();
      await load();
    } catch (err) {
      setMsg(err?.data?.message || "Startgeld konnte nicht gezahlt werden.");
    } finally {
      setBusy(false);
    }
  };

  const seasonJustEnded = Boolean(
    seasonEnd?.ceremony || result?.seasonComplete || result?.seasonCeremony || preview?.seasonComplete
  );

  const continueLabel =
    showTrainingCta
      ? "Weiter zum Training"
      : seasonJustEnded
        ? "Saison abschließen"
        : competition === "friendly" && leagueStarted
          ? "Zum Ligaspiel wechseln"
          : competition === "tournament"
            ? "Nächste Turnierrunde laden"
            : competition === "flowliga"
              ? "Nächstes FlowLiga-Spiel laden"
              : guideNext?.cta || "Nächstes Spiel laden";

  const leagueOppName = leaguePeek
    ? leaguePeek.isHome === false
      ? leaguePeek.home?.name
      : leaguePeek.away?.name
    : null;

  const pickup =
    holdingResult && matchFinished
      ? showTrainingCta
        ? {
            title: "Als Nächstes: Training festlegen",
            hint: report?.focusPlayer
              ? `${report.focusPlayer.name} hat Doppel liegen gelassen — eine kurze Trainingsentscheidung, dann geht’s in die Liga.`
              : "Eine Trainingsentscheidung für die Mannschaft — danach startet die reguläre Liga.",
            primaryLabel: "Zum Training",
            primaryTo: "/manager/training",
            secondaryLabel: null,
            onPrimary: () => {
              setResult(null);
              setMatchdayBoard(null);
              setHoldingResult(false);
            },
          }
        : competition === "friendly" && leagueStarted
          ? {
              title: "Als Nächstes: dein Ligaspiel",
              hint: leagueOppName
                ? `Freundschaft ist durch. Nächster Gegner in der Division: ${leagueOppName}. Aufstellung prüfen, dann anpfeifen.`
                : "Freundschaft ist durch. Die Division wartet — wechsle jetzt zum Ligaspiel.",
              primaryLabel: leagueOppName ? `Ligaspiel gegen ${leagueOppName}` : "Zum Ligaspiel",
              onPrimary: continueToNext,
              secondaryLabel: "Club-Übersicht",
              secondaryTo: "/manager",
            }
          : guideNext?.to && guideNext.to !== "/manager/match"
            ? {
                title: guideNext.title || "Weiter in der Begleitung",
                hint: guideNext.hint || "Der nächste Schritt der Begleitung wartet.",
                primaryLabel: guideNext.cta || "Weiter",
                primaryTo: guideNext.to,
                onPrimary: () => {
                  setResult(null);
                  setMatchdayBoard(null);
                  setHoldingResult(false);
                },
                secondaryLabel: "Nächstes Spiel hier laden",
                onSecondary: continueToNext,
              }
            : seasonJustEnded
              ? {
                  title: "Saison beendet",
                  hint: "Zeremonie mit Preisgeld — danach die Saison-Übersicht mit Highlights und Transfers.",
                  primaryLabel: "Saison abschließen",
                  onPrimary: () => {
                    const c = result?.seasonCeremony || seasonEnd?.ceremony;
                    if (c) {
                      setSeasonEnd({
                        ceremony: c,
                        review: result?.seasonReview || seasonEnd?.review || null,
                      });
                    }
                    setHoldingResult(false);
                    setResult(null);
                    setMatchdayBoard(null);
                    void refresh();
                  },
                }
              : {
                  title:
                    competition === "tournament"
                      ? "Weiter im Team-Turnier"
                      : competition === "flowliga"
                        ? "Weiter in der FlowLiga"
                        : "Weiter geht’s",
                  hint:
                    competition === "tournament"
                      ? "Nächste K.-o.-Runde in diesem Turnier laden — du bleibst hier, nicht in der Liga."
                      : "Lade das nächste angesetzte Spiel in diesem Wettbewerb.",
                  primaryLabel: continueLabel,
                  onPrimary: continueToNext,
                }
      : null;

  const saveLineupFromSheet = async (starterKeys) => {
    setLineupBusy(true);
    setMsg("");
    try {
      await vw.setLineup(userId, starterKeys);
      await refresh();
      const res = await vw.nextMatch(userId, competition);
      setPreview(res.data);
      setMsg("Aufstellung auf dem Bogen gespeichert — bereit zum Anpfiff.");
    } catch (err) {
      setMsg(err?.data?.message || err?.message || "Aufstellung konnte nicht gespeichert werden.");
    } finally {
      setLineupBusy(false);
    }
  };

  const orderByKeys = (players, keys) => {
    if (!keys?.length) return players || [];
    const map = Object.fromEntries((players || []).map((p) => [p.playerKey, p]));
    return keys.map((k) => map[k]).filter(Boolean);
  };
  const sheetHomePlayers = preview?.homePlayers?.length
    ? preview.homePlayers
    : myIsHome
      ? (manager.players || []).filter((p) => (manager.club.starterKeys || []).includes(p.playerKey))
      : preview?.homePlayers || [];
  const sheetAwayPlayers = preview?.awayPlayers?.length
    ? preview.awayPlayers
    : !myIsHome
      ? (manager.players || []).filter((p) => (manager.club.starterKeys || []).includes(p.playerKey))
      : preview?.awayPlayers || [];
  const orderedHome = myIsHome
    ? orderByKeys(sheetHomePlayers, manager.club.starterKeys)
    : sheetHomePlayers;
  const orderedAway = !myIsHome
    ? orderByKeys(sheetAwayPlayers, manager.club.starterKeys)
    : sheetAwayPlayers;
  const homeName =
    preview?.home?.displayName ||
    preview?.home?.name ||
    (myIsHome ? manager.club.displayName || manager.club.name : "Heim");
  const awayName =
    preview?.away?.displayName ||
    preview?.away?.name ||
    (!myIsHome ? manager.club.displayName || manager.club.name : "Gegner");
  const squad = (manager.players || []).filter((p) => !p.loanedOut);
  const isFriendlyView = competition === "friendly" || manager.calendar?.mode === "friendly";
  const seasonNumber =
    preview?.seasonNumber || result?.seasonNumber || manager.seasonNumber || 1;
  const matchday =
    preview?.matchday ||
    result?.matchday ||
    match?.matchday ||
    manager.matchday ||
    1;
  const calendarLabel = isFriendlyView
    ? manager.calendar?.label || "Vorsaison · Freundschaft"
    : `Saison ${seasonNumber} · Spieltag ${matchday}`;

  const showLocalSeasonOverlay = Boolean(
    seasonEnd?.ceremony &&
      !manager?.seasonCeremony &&
      (holdingResult || live || Boolean(result?.seasonCeremony))
  );
  const showLocalCupOverlay = Boolean(
    cupCeremonyLocal &&
      !manager?.cupCeremony &&
      !manager?.seasonCeremony &&
      !showLocalSeasonOverlay
  );

  return (
    <div className="space-y-6">
      {showLocalSeasonOverlay ? (
        <SeasonEndOverlay
          userId={userId}
          ceremony={seasonEnd.ceremony}
          review={seasonEnd.review}
          onRefresh={refresh}
          onDone={() => {
            setSeasonEnd(null);
            setHoldingResult(false);
            setResult(null);
            void refresh().then(() => load());
          }}
        />
      ) : null}
      {showLocalCupOverlay ? (
        <CupCeremonyOverlay
          userId={userId}
          ceremony={cupCeremonyLocal}
          onRefresh={refresh}
          onDone={() => {
            setCupCeremonyLocal(null);
            void refresh();
          }}
        />
      ) : null}
      {holdingResult && matchFinished ? (
        <div ref={resultTopRef} className="scroll-mt-2 space-y-4">
          <MatchResultScreen
            headline={
              report?.headline ||
              (report?.won
                ? myIsHome
                  ? "Heimsieg!"
                  : "Auswärtssieg!"
                : report?.draw
                  ? "Unentschieden"
                  : "Spiel beendet")
            }
            scoreLabel={scoreLabel(match, false, result?.competition || competition)}
            homeName={preview?.home?.displayName || preview?.home?.name || match?.homeName || "Heim"}
            awayName={preview?.away?.displayName || preview?.away?.name || match?.awayName || "Gast"}
            scoreHome={match?.scoreHome}
            scoreAway={match?.scoreAway}
            won={report?.won === true ? true : report?.won === false && !report?.draw ? false : null}
            moneyEarned={moneyTotalDisplay}
            moneyBreakdown={moneyBreakdownDisplay}
            moneyHint={
              moneyTotalDisplay != null
                ? moneyNetDisplay != null && moneyNetDisplay !== moneyTotalDisplay
                  ? `Kasse ${moneyNetDisplay > 0 ? "+" : ""}${moneyNetDisplay.toLocaleString(
                      "de-DE"
                    )} € nach Gehältern (Spielstätte steckt in der Heimquote)`
                  : Number(moneyBreakdownDisplay?.homeCrowd) > 0
                    ? "Heimquote inkl. Spielstätten-Upgrades"
                    : "Liga, Heim und Sponsor"
                : report?.won && (result?.competition || competition) !== "regular"
                  ? "Spielstätten-Geld gibt’s in der Liga (Heimspiele)"
                  : ""
            }
            body={report?.body || ""}
            journey={journeySteps}
            nextTitle={pickup?.title || "Weiter"}
            nextHint={pickup?.hint || "Ein Schritt nach dem anderen — nichts auslassen."}
            primaryLabel={pickup?.primaryLabel || continueLabel}
            primaryTo={pickup?.primaryTo}
            onPrimary={pickup?.onPrimary || continueToNext}
            secondaryLabel={pickup?.secondaryLabel}
            secondaryTo={pickup?.secondaryTo}
            onSecondary={pickup?.onSecondary}
            busy={busy}
            detailsOpen={showResultDetails}
            onToggleDetails={() => setShowResultDetails((v) => !v)}
          />
          {report?.playerHighlights?.length ? (
            <MatchHighlights rows={report.playerHighlights} />
          ) : null}
          {matchdayBoard?.matches?.length ? (
            <MatchdayProgress board={matchdayBoard} polling={boardPolling && !matchdayBoard.complete} />
          ) : null}
          {showResultDetails ? (
            <div className="space-y-4">
              <Spielberichtsbogen
                homeName={preview?.home?.name || match?.homeName || "Heim"}
                awayName={preview?.away?.name || match?.awayName || "Gast"}
                homePlayers={preview?.homePlayers || []}
                awayPlayers={preview?.awayPlayers || []}
                isHome={myIsHome}
                games={result?.match?.singles || match?.singles || []}
                competitionLabel={activeMeta.label}
                seasonNumber={seasonNumber}
                matchday={matchday}
                scoreHome={match?.scoreHome}
                scoreAway={match?.scoreAway}
                editable={false}
              />
              <MatchReportCard report={report} hideNext />
            </div>
          ) : null}
        </div>
      ) : null}

      {live && result?.match && !holdingResult ? (
        <div className="space-y-4">
          <div className="rounded-3xl border border-amber-300 bg-amber-50 dark:bg-gray-800 p-5">
            <div className="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-1">Live</div>
            <h2 className="text-3xl font-black mb-2">Begegnung läuft …</h2>
            <p className="opacity-80 mb-4">Folge dem Ticker — oder spring direkt zum Ergebnis.</p>
            <button
              type="button"
              disabled={busy}
              onClick={() => {
                setRevealed(true);
                setLive(false);
                setHoldingResult(true);
              }}
              className="px-5 py-3 rounded-xl bg-amber-700 text-amber-50 font-semibold"
            >
              Zum Ergebnis springen
            </button>
          </div>
          <MatchTicker
            events={ticker}
            live={live}
            playKey={`${playKey}-${result.match?.id || result.match?._id || "match"}`}
            mySide={mySide}
            homeLabel={preview?.home?.name || match?.homeName || "Heim"}
            awayLabel={preview?.away?.name || match?.awayName || "Gast"}
            onComplete={onTickerDone}
          />
        </div>
      ) : null}

      {!holdingResult && !live ? (
        <>
      <div className="rounded-3xl border border-amber-300 bg-amber-50 dark:bg-gray-800 p-5">
        <div className="text-xs uppercase tracking-wide text-amber-700 dark:text-amber-300 mb-1">
          Wettbewerbe · Phase {phase}
        </div>
        <div className="text-3xl md:text-4xl font-black text-amber-800 dark:text-amber-200 mb-2 tracking-tight">
          {calendarLabel}
        </div>
        <h2 className="text-3xl font-black mb-2">
          {firstFriendlySetup
            ? "Freundschaftsspiel — Aufstellung auf dem Bogen"
            : firstCompetitionPhase
              ? "Dein erster Wettbewerb"
              : "Dein nächster Wettbewerb"}
        </h2>
        {firstFriendlySetup ? (
          <p className="opacity-80">
            Trage H1–H4 in den Spielberichtsbogen ein. H1 spielt das erste Einzel.
          </p>
        ) : null}
        {!firstCompetitionPhase && phase >= 3 ? (
          <p className="opacity-80">
            {phase === 3
              ? leagueStarted
                ? "Schritt für Schritt: die reguläre Liga startet"
                : `Die Liga startet bei ${comps.worldNeeded || 8} Vereinen — bis dahin Freundschaftsspiele.`
              : "Alle Wettbewerbe sind freigeschaltet. Wähle unten den relevanten."}
          </p>
        ) : null}
        {!firstCompetitionPhase && phase === 2 ? (
          <p className="opacity-80">
            Spielbericht und Training zuerst. Die reguläre Liga wird danach freigeschaltet.
          </p>
        ) : null}
      </div>

      {!firstFriendlySetup ? (
        <div className="grid md:grid-cols-2 gap-3">
          {openList.map((item) => {
            const featured = item.id === competition;
            const card = manager?.matchCenter?.[item.id] || {};
            const stand = card.stand || comps.hints?.[item.id] || item.short;
            const prize = card.prize || "";
            const detail = card.detail || "";
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => pickCompetition(item.id)}
                className={`text-left rounded-2xl border p-4 transition ${
                  featured
                    ? "bg-gray-900 text-white border-yellow-400 ring-2 ring-yellow-300"
                    : "bg-white dark:bg-gray-700 border-gray-300 hover:border-amber-400"
                }`}
              >
                <div className="text-xs uppercase opacity-70 mb-1">
                  {item.id === primary ? "Jetzt relevant" : "Freigeschaltet"}
                </div>
                <div className="text-xl font-black">
                  {item.emoji} {item.label}
                </div>
                <p className={`text-sm mt-1 font-semibold ${featured ? "text-yellow-200" : "text-amber-800 dark:text-amber-200"}`}>
                  {stand}
                </p>
                {prize ? (
                  <p className={`text-sm mt-1 ${featured ? "opacity-90" : "opacity-80"}`}>
                    🏆 {prize}
                  </p>
                ) : null}
                {detail ? (
                  <p className={`text-xs mt-1 ${featured ? "opacity-70" : "opacity-60"}`}>{detail}</p>
                ) : (
                  <p className={`text-xs mt-1 ${featured ? "opacity-60" : "opacity-50"}`}>
                    {comps.hints?.[item.id] || item.short}
                  </p>
                )}
              </button>
            );
          })}
        </div>
      ) : null}

      {msg ? (
        <p className={msg.includes("gespeichert") ? "text-emerald-700 font-semibold" : "text-red-600"}>{msg}</p>
      ) : null}

      {!isOpen(competition) ? (
        <p className="opacity-80">Dieser Wettbewerb ist noch nicht freigeschaltet.</p>
      ) : (
        <>
          {needEntry ? (
            <div className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow">
              {preview?.tournament?.entryOpen && preview?.tournament?.entryClosesAt ? (
                <p className="mb-3 rounded-xl border border-yellow-400 bg-yellow-50 dark:bg-yellow-900/30 p-3 text-sm">
                  Anmeldung offen
                  {preview.tournament.fieldSize
                    ? ` · Feld ${preview.tournament.entrants || 0}/${preview.tournament.fieldSize}`
                    : ""}
                  {" — "}
                  startet bei vollem Feld oder spätestens{" "}
                  {formatManagerDate(preview.tournament.entryClosesAt)} (
                  {preview.tournament.entryClosesRelative || relativeUntil(preview.tournament.entryClosesAt)}
                  ).
                </p>
              ) : preview?.tournament && preview.tournament.entryOpen === false ? (
                <p className="mb-3 rounded-xl border border-red-300 bg-red-50 dark:bg-red-900/20 p-3 text-sm">
                  Anmeldung geschlossen
                  {preview.tournament.startedAt
                    ? ` — Turnier läuft seit ${formatManagerDate(preview.tournament.startedAt)}`
                    : ""}
                  .
                </p>
              ) : null}
              <p className="mb-3">
                Fürs Team-Turnier zahlst du {money(preview?.tournament?.entryFee)} Startgeld.
                Einzelne Siege bringen kein Extra-Geld — der gesamte Topf (Startgeld × Teilnehmer) geht an Platz 1–3
                (50&nbsp;% / 30&nbsp;% / 20&nbsp;%). Jede Runde zählt die Checkout-Summe.
              </p>
              <button
                disabled={busy || preview?.tournament?.entryOpen === false}
                onClick={enter}
                className="px-4 py-2 bg-green-500 text-white border border-yellow-400 rounded font-semibold disabled:opacity-50"
              >
                {preview?.tournament?.entryOpen === false ? "Zu spät für dieses Turnier" : "Startgeld zahlen"}
              </button>
              <p className="text-sm mt-3 opacity-70">
                Übersicht unter{" "}
                <Link className="underline" to="/manager/league?tab=tournament">
                  Liga → Team-Turniere
                </Link>
                .
              </p>
            </div>
          ) : null}

          {preview?.awaitingTournamentStart ||
          (competition === "tournament" &&
            preview?.tournamentEntered &&
            preview?.tournament?.entryOpen) ? (
            <div className="rounded-xl border border-yellow-400 bg-yellow-50 dark:bg-yellow-900/20 p-4 text-sm">
              Du bist angemeldet. Das Team-Turnier startet, sobald das Feld voll ist
              {preview?.tournament?.fieldSize
                ? ` (${preview.tournament.entrants || 0}/${preview.tournament.fieldSize})`
                : ""}
              {preview?.tournament?.entryClosesAt
                ? ` — spätestens ${
                    preview.tournament.entryClosesLabel ||
                    formatManagerDate(preview.tournament.entryClosesAt)
                  }${
                    preview.tournament.entryClosesRelative ||
                    relativeUntil(preview.tournament.entryClosesAt)
                      ? ` (${preview.tournament.entryClosesRelative || relativeUntil(preview.tournament.entryClosesAt)})`
                      : ""
                  }`
                : ""}
              . Bis dahin kein K.-o.-Spiel — Aufstellung kannst du trotzdem vorbereiten.
            </div>
          ) : null}

          {preview?.eliminated ? (
            <div className="bg-white dark:bg-gray-700 rounded-lg p-6 shadow">
              <p className="font-semibold mb-2">Dein Team ist in dieser Runde ausgeschieden.</p>
              <p className="text-sm opacity-70">
                Den Baum siehst du unter <Link className="underline" to="/manager/league">Liga → Team-Turniere</Link>.
              </p>
            </div>
          ) : null}

          {preview?.waitingForNextRound && !preview?.eliminated ? (
            <p className="text-sm opacity-80">Die nächste K.-o.-Runde wird gelost, sobald die anderen Spiele der Runde durch sind.</p>
          ) : null}

          {showPreMatchSheet && (firstFriendlySetup || previewLoading || playableScheduled) ? (
            <div ref={sheetRef} className="space-y-4 scroll-mt-4">
              <div className="bg-white dark:bg-gray-700 rounded-3xl p-6 shadow border border-amber-200/50">
                <h2 className="text-3xl font-bold mb-2">
                  {activeMeta.emoji}{" "}
                  {preview?.roundLabel ? `${preview.roundLabel}: ` : null}
                  {homeName} vs {awayName}
                </h2>
                <p className="opacity-70 mb-2">{scoreLabel(match, hideScore, competition)}</p>
                <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
                  {firstFriendlySetup
                    ? "Schritt 1: Trage deine Aufstellung in den Spielberichtsbogen ein (H1–H4). H1 spielt das erste Einzel — z. B. Lea."
                    : competition === "regular"
                      ? "Ligaspiel: Aufstellung H1–H4 prüfen, dann live oder schnell spielen."
                      : "Trage deine Vier auf dem Spielberichtsbogen ein (H1–H4 bzw. G1–G4). H1 spielt das erste Einzel."}
                </p>
              </div>
              <Spielberichtsbogen
                homeName={homeName}
                awayName={awayName}
                homePlayers={orderedHome}
                awayPlayers={orderedAway}
                squad={squad}
                isHome={myIsHome}
                competitionLabel={activeMeta.label}
                seasonNumber={seasonNumber}
                matchday={matchday}
                editable
                saving={lineupBusy}
                onSaveLineup={saveLineupFromSheet}
                hint={
                  myIsHome
                    ? "Dein Team links (H1–H4). Wen du als H1 setzt, spielt das erste Einzel gegen G1."
                    : "Dein Team rechts (G1–G4). G1 spielt das erste Einzel gegen H1."
                }
                afterSaveActions={
                  playableScheduled && !needEntry ? (
                    <>
                      <button
                        type="button"
                        disabled={busy || !hasLineup}
                        onClick={() => play("live")}
                        className="px-4 py-2 bg-green-500 text-white border border-yellow-400 rounded text-sm font-bold disabled:opacity-50"
                        title={!hasLineup ? "Zuerst Aufstellung speichern" : undefined}
                      >
                        📡 Live erleben
                      </button>
                      <button
                        type="button"
                        disabled={busy || !hasLineup}
                        onClick={() => play("quick")}
                        className="px-4 py-2 bg-gray-900 text-white rounded text-sm font-semibold disabled:opacity-50"
                        title={!hasLineup ? "Zuerst Aufstellung speichern" : undefined}
                      >
                        ⚡ Schnell simulieren
                      </button>
                      {!guided || phase >= 3 ? (
                        <button
                          type="button"
                          disabled={busy || !hasLineup}
                          onClick={() => play("result")}
                          className="px-4 py-2 bg-gray-200 rounded text-sm font-semibold disabled:opacity-50"
                          title={!hasLineup ? "Zuerst Aufstellung speichern" : undefined}
                        >
                          📋 Nur Ergebnis
                        </button>
                      ) : null}
                    </>
                  ) : null
                }
              />
              {!hasLineup && playableScheduled ? (
                <p className="text-sm rounded-xl border border-amber-300 bg-amber-50 dark:bg-gray-800 p-3">
                  Speichere zuerst die Aufstellung — danach sind die Spiel-Buttons daneben aktiv.
                </p>
              ) : null}
            </div>
          ) : !needEntry && !preview?.eliminated && !previewLoading ? (
            <div className="rounded-2xl border border-amber-300 bg-amber-50 dark:bg-gray-800 p-5">
              <h3 className="text-xl font-black mb-2">
                {competition === "regular"
                  ? match?.status === "finished"
                    ? "Nächster Spieltag kommt"
                    : "Ligaspiel wird vorbereitet …"
                  : competition === "tournament"
                    ? "Nächste Turnierrunde noch nicht bereit"
                    : "Kein Spiel in diesem Wettbewerb"}
              </h3>
              <p className="opacity-80 mb-4">
                {competition === "regular"
                  ? "Dein nächstes Ligaspiel wird jetzt angesetzt (AI-Spiele des alten Tags laufen parallel — darauf wartest du nicht). Einmal neu laden."
                  : competition === "tournament"
                    ? "Andere K.-o.-Spiele der Runde müssen erst durch sein. Bleib im Team-Turnier und lade gleich nochmal — du musst nicht in die Liga wechseln."
                    : "Wechsle den Wettbewerb oder lade die Seite neu."}
              </p>
              <button
                type="button"
                disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  setMsg("");
                  try {
                    setResult(null);
                    setHoldingResult(false);
                    setPreviewLoading(true);
                    userPickedComp.current = true;
                    const target = competition === "regular" ? "regular" : competition;
                    let res = await vw.nextMatch(userId, target);
                    if (
                      target === "regular" &&
                      (!res.data?.match || res.data.match.status !== "scheduled")
                    ) {
                      await new Promise((r) => setTimeout(r, 600));
                      res = await vw.nextMatch(userId, "regular");
                    }
                    setPreview(res.data);
                    if (!res.data?.match || res.data.match.status !== "scheduled") {
                      setMsg(
                        target === "tournament"
                          ? "Noch kein Turnierspiel angesetzt. Später erneut laden — Tab bleibt Team-Turnier."
                          : "Spielplan konnte nicht ergänzt werden. Bitte Seite neu laden (Ctrl+F5)."
                      );
                    } else {
                      setMsg("");
                    }
                  } catch (err) {
                    setMsg(err?.data?.message || err?.message || "Konnte kein Spiel laden.");
                  } finally {
                    setPreviewLoading(false);
                    setBusy(false);
                  }
                }}
                className="px-5 py-3 rounded-xl bg-emerald-700 text-white font-semibold"
              >
                {competition === "tournament"
                  ? "Turnier erneut laden"
                  : competition === "flowliga"
                    ? "FlowLiga erneut laden"
                    : "Nächsten Spieltag laden"}
              </button>
            </div>
          ) : null}

          {busy ? (
            <p className="text-sm opacity-70 animate-pulse">Match wird berechnet …</p>
          ) : null}

          {/* Anpfiff-Buttons sitzen am Spielberichtsbogen neben „Aufstellung speichern“ */}
          {canPlay && !showPreMatchSheet ? (
            <div className="flex flex-wrap gap-3">
              <button disabled={busy} onClick={() => play("live")} className="px-4 py-2 bg-green-500 text-white border border-yellow-400 rounded-xl font-semibold">
                📡 Live erleben
              </button>
              <button disabled={busy} onClick={() => play("quick")} className="px-4 py-2 bg-gray-900 text-white rounded-xl">
                ⚡ Schnell simulieren
              </button>
              {!guided || phase >= 3 ? (
                <button disabled={busy} onClick={() => play("result")} className="px-4 py-2 bg-gray-200 rounded-xl">
                  📋 Nur Ergebnis
                </button>
              ) : null}
            </div>
          ) : null}
        </>
      )}
        </>
      ) : null}
    </div>
  );
};

export default ManagerMatch;
